import React, { useState, useEffect } from 'react';
import { Analysis, Match, BetSuggestion } from '../../types';
import { matchesAPI } from '../../services/api';
import { useCartStore } from '../../store';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { FaTimes, FaShoppingCart, FaSpinner, FaExclamationTriangle, FaCheckCircle, FaBolt } from 'react-icons/fa';
import toast from 'react-hot-toast';

interface Props {
  match: Match;
  onClose: () => void;
}

const RISK_COLORS = { BAJO: '#10b981', MEDIO: '#f59e0b', ALTO: '#ef4444' };
const PIE_COLORS = ['#6366f1', '#475569', '#10b981'];

const RiskBadge: React.FC<{ riesgo: string }> = ({ riesgo }) => (
  <span className={`risk-${riesgo.toLowerCase()} text-xs`}>{riesgo}</span>
);

const EV_STYLES: Record<string, string> = {
  ALTO:     'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
  MEDIO:    'bg-blue-500/20 text-blue-300 border border-blue-500/40',
  BAJO:     'bg-slate-600/40 text-slate-400 border border-slate-500/30',
  NEGATIVO: 'bg-red-500/10 text-red-400 border border-red-500/20',
};

const EVBadge: React.FC<{ ev?: number; ev_label?: string }> = ({ ev, ev_label }) => {
  if (ev === undefined || ev === null) return null;
  const label = ev_label || (ev >= 0.10 ? 'ALTO' : ev >= 0.05 ? 'MEDIO' : ev >= 0 ? 'BAJO' : 'NEGATIVO');
  const style = EV_STYLES[label] || EV_STYLES.BAJO;
  const sign  = ev >= 0 ? '+' : '';
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${style}`} title="Valor Esperado (EV)">
      EV {sign}{(ev * 100).toFixed(1)}%
    </span>
  );
};

const BetCard: React.FC<{ bet: BetSuggestion; match: Match; onAdd: () => void }> = ({ bet, match, onAdd }) => (
  <div className={`border rounded-lg p-3 hover:border-indigo-500/50 transition-all ${(bet.ev ?? 0) >= 0.05 ? 'border-emerald-600/40 bg-emerald-950/10' : 'border-slate-700'}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs text-slate-500 uppercase">{bet.tipo}</span>
          <RiskBadge riesgo={bet.riesgo} />
          <EVBadge ev={bet.ev} ev_label={bet.ev_label} />
        </div>
        <div className="font-medium text-white text-sm">{bet.descripcion}</div>
        {bet.razon && <div className="text-xs text-slate-400 mt-1">{bet.razon}</div>}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-yellow-400 font-mono font-bold">{bet.cuota_esperada?.toFixed(2)}</div>
        <div className="text-emerald-400 text-xs">{Number.isFinite(bet.probabilidad_acierto) ? bet.probabilidad_acierto : '—'}%</div>
      </div>
    </div>
    <div className="mt-2 flex items-center justify-between">
      <div className="flex-1 bg-slate-700 rounded-full h-1 mr-2">
        <div
          className={`h-1 rounded-full ${(bet.probabilidad_acierto ?? 0) >= 70 ? 'bg-emerald-500' : (bet.probabilidad_acierto ?? 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
          style={{ width: `${Number.isFinite(bet.probabilidad_acierto) ? bet.probabilidad_acierto : 0}%` }}
        />
      </div>
      <button onClick={onAdd} className="btn-primary text-xs py-1 px-2 flex items-center gap-1">
        <FaShoppingCart size={10} />
        Agregar
      </button>
    </div>
  </div>
);

const TABS = ['Probabilidades', 'Apuestas', 'Factores', 'Análisis IA'];

// Sport-specific display config
const SPORT_CONFIG: Record<string, {
  section: string;
  bars: { label: string; key: keyof NonNullable<Analysis['goles_esperados']> }[];
  showBtts: boolean;
}> = {
  football: {
    section: 'Goles Esperados',
    bars: [
      { label: 'Over 1.5', key: 'probabilidad_over_1_5' },
      { label: 'Over 2.5', key: 'probabilidad_over_2_5' },
      { label: 'Over 3.5', key: 'probabilidad_over_3_5' },
    ],
    showBtts: true,
  },
  basketball: {
    section: 'Puntos Esperados',
    bars: [
      { label: 'Más de 200.5 pts', key: 'probabilidad_over_1_5' },
      { label: 'Más de 210.5 pts', key: 'probabilidad_over_2_5' },
      { label: 'Más de 220.5 pts', key: 'probabilidad_over_3_5' },
    ],
    showBtts: false,
  },
  american_football: {
    section: 'Puntos Esperados',
    bars: [
      { label: 'Más de 40.5 pts', key: 'probabilidad_over_1_5' },
      { label: 'Más de 45.5 pts', key: 'probabilidad_over_2_5' },
      { label: 'Más de 50.5 pts', key: 'probabilidad_over_3_5' },
    ],
    showBtts: false,
  },
  baseball: {
    section: 'Carreras Esperadas',
    bars: [
      { label: 'Over 7.5 carreras', key: 'probabilidad_over_1_5' },
      { label: 'Over 8.5 carreras', key: 'probabilidad_over_2_5' },
      { label: 'Over 9.5 carreras', key: 'probabilidad_over_3_5' },
    ],
    showBtts: true,
  },
  hockey: {
    section: 'Goles Esperados',
    bars: [
      { label: 'Over 4.5 goles', key: 'probabilidad_over_1_5' },
      { label: 'Over 5.5 goles', key: 'probabilidad_over_2_5' },
      { label: 'Over 6.5 goles', key: 'probabilidad_over_3_5' },
    ],
    showBtts: true,
  },
};

const getSportConfig = (deporte?: string) =>
  SPORT_CONFIG[deporte || 'football'] || SPORT_CONFIG.football;

const AnalysisModal: React.FC<Props> = ({ match, onClose }) => {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const { addItem } = useCartStore();
  const sportCfg = getSportConfig(match.deporte);

  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        setLoading(true);
        const res = await matchesAPI.getAnalysis(match.id);
        setAnalysis(res.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al cargar el análisis');
      } finally {
        setLoading(false);
      }
    };
    loadAnalysis();
  }, [match.id]);

  const handleAddBet = (bet: BetSuggestion) => {
    addItem({ match, suggestion: bet, cantidad: 10 });
    toast.success(`Apuesta agregada al carrito`);
  };

  const pieData = analysis ? [
    { name: match.local_nombre, value: analysis.probabilidades.local_gana },
    { name: 'Empate', value: analysis.probabilidades.empate },
    { name: match.visitante_nombre, value: analysis.probabilidades.visitante_gana }
  ] : [];

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content animate-fade-in">
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                {match.local_nombre} <span className="text-slate-500">vs</span> {match.visitante_nombre}
              </h2>
              <p className="text-sm text-slate-400">{match.liga} · {new Date(match.fecha_hora).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-2">
              <FaTimes size={20} />
            </button>
          </div>

          {analysis && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {/* Source badge — most prominent */}
              {analysis.fuente_analisis === 'IA' ? (
                <span className="text-xs bg-indigo-500/25 text-indigo-300 border border-indigo-500/40 px-2 py-0.5 rounded font-semibold">
                  🤖 Análisis IA
                </span>
              ) : (
                <span className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded font-semibold">
                  📐 Modelo Estadístico
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <FaBolt className="text-yellow-400" size={12} />
                <span className="text-xs text-slate-300">Confianza: <strong className="text-white">{analysis.confianza_analisis}%</strong></span>
              </div>
              {analysis.marcador_probable && (
                <span className="text-xs text-slate-400">Marcador probable: <strong className="text-white">{analysis.marcador_probable}</strong></span>
              )}
              {analysis.datos_reales && (
                <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded">
                  ✅ ESPN
                </span>
              )}
              {analysis.datos_suficientes && (
                <span className="text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded">
                  📊 Poisson
                </span>
              )}
              {analysis.elo_signal && (
                <span className="text-xs bg-violet-500/20 text-violet-300 border border-violet-500/30 px-2 py-0.5 rounded" title={`ELO Local: ${analysis.elo_signal.local_rating} | ELO Visitante: ${analysis.elo_signal.visitante_rating}`}>
                  ⚡ ELO
                </span>
              )}
              {analysis.market_signal && (
                <span className="text-xs bg-teal-500/20 text-teal-300 border border-teal-500/30 px-2 py-0.5 rounded" title={`Vig: ${analysis.market_signal.probs.vig}% | ${analysis.market_signal.bookmakers?.join(', ')}`}>
                  💹 Mercado
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700 px-6">
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === i ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="flex flex-col items-center py-12 gap-4">
              <FaSpinner className="animate-spin text-indigo-400" size={32} />
              <p className="text-slate-400">Generando análisis con IA...</p>
              <p className="text-xs text-slate-500">Procesando datos históricos y estadísticas</p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <FaExclamationTriangle className="text-red-400" />
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {analysis && !loading && (
            <>
              {/* Tab 0: Probabilidades */}
              {activeTab === 0 && (
                <div className="space-y-6">
                  {/* ELO + Market signals row */}
                  {(analysis.elo_signal || analysis.market_signal) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {analysis.elo_signal && (
                        <div className="bg-violet-950/30 border border-violet-700/30 rounded-lg p-3">
                          <div className="text-xs font-semibold text-violet-400 mb-2">⚡ Señal ELO</div>
                          <div className="flex justify-between text-xs text-slate-300 mb-1">
                            <span>{match.local_nombre}</span>
                            <span className="font-mono font-bold text-white">{analysis.elo_signal.local_rating}</span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-300 mb-2">
                            <span>{match.visitante_nombre}</span>
                            <span className="font-mono font-bold text-white">{analysis.elo_signal.visitante_rating}</span>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <span className="text-emerald-400">{analysis.elo_signal.probs.local_gana}%</span>
                            <span className="text-slate-500">{analysis.elo_signal.probs.empate}%</span>
                            <span className="text-red-400">{analysis.elo_signal.probs.visitante_gana}%</span>
                            <span className="ml-auto text-slate-500">conf. {analysis.elo_signal.confianza}%</span>
                          </div>
                        </div>
                      )}
                      {analysis.market_signal && (
                        <div className="bg-teal-950/30 border border-teal-700/30 rounded-lg p-3">
                          <div className="text-xs font-semibold text-teal-400 mb-2">💹 Señal de Mercado</div>
                          <div className="flex justify-between text-xs text-slate-300 mb-1">
                            <span>Cuotas (L/E/V)</span>
                            <span className="font-mono text-yellow-400">
                              {analysis.market_signal.odds.h} / {analysis.market_signal.odds.d ?? '—'} / {analysis.market_signal.odds.a}
                            </span>
                          </div>
                          <div className="flex gap-2 text-xs mb-1">
                            <span className="text-emerald-400">{analysis.market_signal.probs.local_gana}%</span>
                            <span className="text-slate-500">{analysis.market_signal.probs.empate}%</span>
                            <span className="text-red-400">{analysis.market_signal.probs.visitante_gana}%</span>
                          </div>
                          <div className="text-xs text-slate-500">Vig: {analysis.market_signal.probs.vig}% · {analysis.market_signal.bookmakers?.length} casas</div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-300 mb-4">Probabilidades</h3>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                            {pieData.map((_, index) => <Cell key={index} fill={PIE_COLORS[index]} />)}
                          </Pie>
                          <Tooltip formatter={(value: any) => [`${value}%`, '']} contentStyle={{ background: '#1e293b', border: '1px solid #334155' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-4 mt-2">
                        {pieData.map((d, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i] }} />
                            <span className="text-xs text-slate-400">{d.name}: {d.value}%</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-slate-300">{sportCfg.section}</h3>
                      {[
                        { label: match.local_nombre, value: analysis.goles_esperados?.local },
                        { label: match.visitante_nombre, value: analysis.goles_esperados?.visitante },
                        { label: 'Total', value: analysis.goles_esperados?.total },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between items-center">
                          <span className="text-xs text-slate-400">{label}</span>
                          <span className="font-bold text-white">{typeof value === 'number' ? value.toFixed(1) : '—'}</span>
                        </div>
                      ))}
                      <div className="border-t border-slate-700 pt-3 space-y-2">
                        {sportCfg.bars.map(({ label, key }) => {
                          const value = (analysis.goles_esperados as any)?.[key];
                          return (
                            <div key={label} className="flex items-center gap-2">
                              <span className="text-xs text-slate-400 w-28 truncate">{label}</span>
                              <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                                <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${value ?? 0}%` }} />
                              </div>
                              <span className="text-xs text-white w-8 text-right">{value ?? '—'}%</span>
                            </div>
                          );
                        })}
                        {sportCfg.showBtts && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-28">BTTS</span>
                            <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                              <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${analysis.goles_esperados?.probabilidad_btts ?? 0}%` }} />
                            </div>
                            <span className="text-xs text-white w-8 text-right">{analysis.goles_esperados?.probabilidad_btts ?? '—'}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 1: Apuestas */}
              {activeTab === 1 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-300 mb-4">
                    {analysis.apuestas_recomendadas?.length || 0} Apuestas Recomendadas
                  </h3>
                  {analysis.apuestas_recomendadas?.map((bet, i) => (
                    <BetCard key={i} bet={bet} match={match} onAdd={() => handleAddBet(bet)} />
                  ))}
                </div>
              )}

              {/* Tab 2: Factores */}
              {activeTab === 2 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                      <FaCheckCircle /> Factores Clave
                    </h3>
                    <ul className="space-y-2">
                      {analysis.factores_clave?.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="text-emerald-400 mt-0.5">•</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
                      <FaExclamationTriangle /> Factores de Riesgo
                    </h3>
                    <ul className="space-y-2">
                      {analysis.factores_riesgo?.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="text-red-400 mt-0.5">•</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Tab 3: Análisis IA */}
              {activeTab === 3 && (
                <div className="space-y-4">
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-indigo-400 mb-3">Análisis Narrativo</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {analysis.analisis_narrativo || 'Análisis generado basado en datos históricos y estadísticas disponibles.'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="stat-card">
                      <div className="text-xs text-slate-400 mb-1">Forma Local</div>
                      <div className="text-sm font-medium text-white">{analysis.forma_local || 'N/D'}</div>
                    </div>
                    <div className="stat-card">
                      <div className="text-xs text-slate-400 mb-1">Forma Visitante</div>
                      <div className="text-sm font-medium text-white">{analysis.forma_visitante || 'N/D'}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisModal;
